import { useCallback, useEffect, useState } from 'react';
import type { InjectedRouter } from 'react-router';

import { BannerPlacement } from '../../../../typings/enums';
import { useAddToast } from '../../../contexts/ToastContext';
import { window } from '../../../globals';
import useOnMount from '../../../hooks/useOnMount';
import BannerManager from '../../BannerManager';
import Loader from '../../generic/Loader';
import { buildRegisterHref } from '../buildRegisterHref';
import SignInSignUpToggle from '../components/SignInSignUpToggle';
import useAuthState from '../hooks/useAuthState';
import AuthLayout from '../layout/AuthLayout';
import { readOrgHint, syncOrgHintFromQuery } from '../orgHint';
import CredentialForm from '../steps/CredentialForm';
import EnterIdentifierStep from '../steps/EnterIdentifierStep';
import MethodsStep from '../steps/MethodsStep';
import MfaStep from '../steps/MfaStep';
import OrgPickerStep from '../steps/OrgPickerStep';
import OrphanSocialStep from '../steps/OrphanSocialStep';
import { SocialOrgPickerStep } from '../steps/SocialOrgPickerStep';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type {
  Candidate,
  OAuthProvider,
  LoginStateResponse,
  ResolveUserResponse,
  SignupSuggestion,
} from '../../../../shared/types/routes/auth/v2';
import type { RouterProps } from '../../../types';

/**
 * Client-side navigation state another auth page can hand this one. `methodsOrg` seeds the flow directly at the
 * methods step for that org — the signup org-picker uses it so "sign in to an existing org" lands on the org's
 * actual sign-in method instead of restarting at the identifier step. State only survives an SPA transition; a
 * refresh falls back to the identifier step with the `?email=` prefill.
 */
export type LoginLocationState = { methodsOrg: Candidate };

export type LoginPageProps = {
  location: RouterProps['location'] & { state?: LoginLocationState };
  /** react-router v3 injects this; only `push` is used, for client-side navigation to the register page. */
  router: Pick<InjectedRouter, 'push'>;
};

export type LoginStep =
  | { kind: 'enter_identifier' }
  | {
      kind: 'pick_org';
      candidates: Candidate[];
      signupSuggestions: SignupSuggestion[];
      // An orphan account on a claimed domain surfaces both signals: the picker offers the domain orgs AND a
      // continue-to-account option. Carried so the render knows whether to offer it and which orphan step to route into.
      hasOrphanAccount: boolean;
      hasPasswordAccount: boolean;
    }
  | { kind: 'pick_org_social'; candidates: Candidate[]; provider: OAuthProvider }
  | { kind: 'methods'; org: Candidate }
  // An existing account with no organization yet: authenticate org-less (no org picked) to resume onboarding.
  | { kind: 'orphan_credential' }
  // A social account with no organization yet and no password: re-authenticate via its provider.
  | { kind: 'orphan_social' }
  | { kind: 'mfa'; mfaMethod: MfaMethodType };

const Login = ({ location, router }: LoginPageProps) => {
  const [identifier, setIdentifier] = useState(location.query.email ?? '');
  // A signup-picker handoff seeds the flow at the chosen org's methods step (see LoginLocationState). A
  // server-held in-progress phase (MFA challenge, pending social pick) still wins via the auth-state effect
  // below — that state is further along than any client-side seed.
  const seededOrg = location.state?.methodsOrg;
  const [step, setStep] = useState<LoginStep>(
    seededOrg ? { kind: 'methods', org: seededOrg } : { kind: 'enter_identifier' },
  );
  const subdomain = location.query.subdomain;
  const { data: authState, isLoading } = useAuthState(subdomain);
  const addToast = useAddToast();
  // The kind of the first auth-state the server reported; null until it resolves.
  const [initialAuthStateKind, setInitialAuthStateKind] = useState<LoginStateResponse['kind'] | null>(null);

  // The idle, mfa, and pending_social phases all carry the configured OAuth buttons, so backing out of a pending
  // challenge or the social org picker to the identifier step still offers social login.
  const oauthProviders =
    authState && (authState.kind === 'idle' || authState.kind === 'mfa' || authState.kind === 'pending_social')
      ? authState.oauthProviders
      : [];

  // With a `?subdomain=`, the mount-time auth-state carries the resolved org (a full candidate). Its presence
  // locks the flow to that org: the identifier step advances straight to its methods, skipping the picker.
  const subdomainOrg = authState?.kind === 'idle' ? authState.subdomainOrg : null;

  // Resume an in-progress login phase after a refresh (or after an OAuth callback lands the user back here): when the
  // mount-time auth-state fetch reports a pending MFA challenge or a pending social org-pick, jump straight to that
  // step. Gated to fire once so backing out of the step (while the server session is still alive) doesn't bounce the
  // user straight back in. Applied during render, so a seeded or `?subdomain=` landing never renders the step it
  // replaces.
  if (initialAuthStateKind === null && authState) {
    setInitialAuthStateKind(authState.kind);
    if (authState.kind === 'mfa') {
      setStep({ kind: 'mfa', mfaMethod: authState.mfaMethod });
    } else if (authState.kind === 'pending_social') {
      setStep({ kind: 'pick_org_social', candidates: authState.candidates, provider: authState.provider });
    }
  }

  // OIDC clients (e.g. fossabot) send ?org_hint=<orgId> so the org pickers can preselect the org their
  // request is for. Stash it tab-scoped: the social round-trip returns to a bare /auth/login, so the URL
  // can't carry it across. A pending social phase IS that same login attempt returning mid-flow, so the
  // stash is kept; any other load without a valid hint clears it (a hint is scoped to the attempt that
  // carried it and must not bleed into a later unhinted login in the same tab).
  useEffect(() => {
    if (initialAuthStateKind !== null && initialAuthStateKind !== 'pending_social') {
      syncOrgHintFromQuery(location.query.org_hint);
    }
  }, [initialAuthStateKind, location.query.org_hint]);

  // A full-page redirect can carry a failure message (e.g. an SSO/OIDC error lands on `/auth/login?loginError=`).
  // Surface it once on mount — the redirect replaced the page, so there is no inline field to attach it to.
  useOnMount(() => {
    const { loginError } = location.query;
    if (loginError) {
      addToast({ type: 'error', content: loginError });
    }
  });

  // Stable identities: these flow into settle effects (useLoginResponseEffect, MfaStep) that must not re-run
  // just because this page re-rendered while a mutation result stays latched. Declared before the loading
  // return below — hooks must run unconditionally.
  const resetToIdentifier = useCallback(() => setStep({ kind: 'enter_identifier' }), []);
  const requireMfa = useCallback((mfaMethod: MfaMethodType) => setStep({ kind: 'mfa', mfaMethod }), []);

  // A `?subdomain=` landing must not flash the unlocked identifier step before the lock is known, and a seeded
  // handoff must not render (or accept input on) its methods step while a server-held phase could still win:
  // hold render until the auth-state resolves (any reported MFA / pending-social phase is applied in the same
  // render, above). Scoped to subdomain/seeded modes so the ordinary login page is unaffected.
  if ((subdomain || seededOrg) && isLoading) {
    return (
      <AuthLayout>
        <Loader />
      </AuthLayout>
    );
  }

  // A real account that never finished onboarding. A confirmable password → log in org-less with it; a social account
  // (no password) → offer its provider to re-authenticate. Either resolves to the org-less account, and the server org
  // guard then routes them to onboarding.
  const goToOrphanStep = (hasPasswordAccount: boolean) =>
    setStep(hasPasswordAccount ? { kind: 'orphan_credential' } : { kind: 'orphan_social' });

  const handleResolved = (response: ResolveUserResponse) => {
    const { candidates, signupSuggestions, hasOrphanAccount, hasPasswordAccount } = response;
    const [firstCandidate] = candidates;

    // The single-org fast path also yields to the picker when an org-less account exists, so the person can
    // still choose "continue with your existing account" and resume onboarding instead of being locked to the org.
    if (firstCandidate && candidates.length === 1 && signupSuggestions.length === 0 && !hasOrphanAccount) {
      setStep({ kind: 'methods', org: firstCandidate });
    } else if (candidates.length > 0 || signupSuggestions.length > 0) {
      // With domain suggestions (and, for an orphan, no candidates) show the picker. `hasOrphanAccount` lets it also
      // offer "continue with your existing account", so an orphan whose email is on a claimed domain can still reach
      // their org-less account and onboarding instead of being forced to join a suggested org.
      setStep({
        kind: 'pick_org',
        candidates,
        signupSuggestions,
        hasOrphanAccount,
        hasPasswordAccount: Boolean(hasPasswordAccount),
      });
    } else if (hasOrphanAccount) {
      goToOrphanStep(Boolean(hasPasswordAccount));
    } else {
      // Client-side navigation: both auth pages live under the same SPA router, and this is pre-authentication, so
      // there's no session change to force a full reload.
      router.push(buildRegisterHref(identifier, location.query.next));
    }
  };

  const advanceToMethods = (org: Candidate) => setStep({ kind: 'methods', org });

  const renderStep = () => {
    switch (step.kind) {
      case 'enter_identifier':
        return (
          <EnterIdentifierStep
            identifier={identifier}
            onIdentifierChange={setIdentifier}
            oauthProviders={oauthProviders}
            subdomainOrg={subdomainOrg}
            next={location.query.next}
            onResolved={handleResolved}
          />
        );
      case 'pick_org':
        return (
          <OrgPickerStep
            candidates={step.candidates}
            signupSuggestions={step.signupSuggestions}
            preferredOrgId={readOrgHint()}
            next={location.query.next}
            onPickCandidate={advanceToMethods}
            onBack={resetToIdentifier}
            onContinueOrphan={step.hasOrphanAccount ? () => goToOrphanStep(step.hasPasswordAccount) : undefined}
          />
        );
      case 'pick_org_social':
        return (
          <SocialOrgPickerStep
            candidates={step.candidates}
            provider={step.provider}
            preferredOrgId={readOrgHint()}
            onMfaRequired={requireMfa}
            onBack={resetToIdentifier}
          />
        );
      case 'methods':
        return (
          <MethodsStep
            org={step.org}
            identifier={identifier}
            next={location.query.next}
            oauthProviders={oauthProviders}
            onMfaRequired={requireMfa}
            onBack={resetToIdentifier}
          />
        );
      case 'orphan_credential':
        return (
          <CredentialForm
            org={null}
            identifier={identifier}
            credentialKind="password"
            showForgotPassword
            next={location.query.next}
            onMfaRequired={requireMfa}
            onBack={resetToIdentifier}
          />
        );
      case 'orphan_social':
        return (
          <OrphanSocialStep
            identifier={identifier}
            providers={oauthProviders}
            next={location.query.next}
            onBack={resetToIdentifier}
          />
        );
      case 'mfa':
        return <MfaStep mfaMethod={step.mfaMethod} next={location.query.next} onBack={resetToIdentifier} />;
      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  };

  return (
    <AuthLayout>
      {renderStep()}
      <SignInSignUpToggle identifier={identifier} next={location.query.next} />
      {window.on_prem && <BannerManager placement={BannerPlacement.Login} />}
    </AuthLayout>
  );
};

export default Login;
